import React from 'react';
import { EligibilityStatus, UwProductSummary } from 'types/uwService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { NavigationClose } from '@insurancemenu/imenu-components-lib/dist/icons';
import { colors } from '@insurancemenu/imenu-components-lib';
import { CartBrowsingCriteria } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { convertRateValue } from 'global/utils/convertRateValue';
import { ICustomizableElement } from '@insurancemenu/imenu-components-lib/dist/types';
import { observer } from 'mobx-react-lite';
import {
  ProductSummaryStyled,
  RatesItemStyled,
  RatesListStyled,
  RatesNameStyled,
  RateNameStyled,
  RateValueStyled,
  NotEligibleRatesStyled,
  BrowsingCriteriaBlockStyled,
  BrowsingCriteriaTextStyled,
  NotEligibleBrowsingCriteriaStyled,
} from './styles';
import { PRODUCT_SUMMARY_TEST_IDS } from '../constants';

export interface ProductSummaryProps extends ICustomizableElement {
  productSummary: UwProductSummary;
  rateTitle: string;
  productLineId: ProductLineId;
  eligibilityStatus?: EligibilityStatus;
  isRenewOrCancelProductLine: boolean;
  sectionRatesQuantity: number;
  $side: 'left' | 'right';
}

const ProductSummary: React.FC<ProductSummaryProps> = observer(
  ({
    productSummary,
    rateTitle,
    productLineId,
    eligibilityStatus,
    isRenewOrCancelProductLine,
    sectionRatesQuantity,
    className,
    $side,
  }) => {
    return (
      <ProductSummaryStyled $side={$side} className={className} data-test-id={PRODUCT_SUMMARY_TEST_IDS.BLOCK}>
        <RatesNameStyled>{rateTitle}</RatesNameStyled>
        <RatesListStyled $eligibilityStatus={eligibilityStatus} data-test-id={PRODUCT_SUMMARY_TEST_IDS.RATES_LIST}>
          {productSummary.rates.length > 0 ? (
            <>
              {productSummary.rates.map((rate) => (
                <RatesItemStyled key={rate.id} data-test-id={PRODUCT_SUMMARY_TEST_IDS.RATES_ITEM}>
                  <RateNameStyled>{rate.displayKey}</RateNameStyled>
                  <RateValueStyled data-test-id={PRODUCT_SUMMARY_TEST_IDS.RATE_VALUE}>
                    {convertRateValue(String(rate.value))}
                  </RateValueStyled>
                </RatesItemStyled>
              ))}
            </>
          ) : (
            <NotEligibleRatesStyled
              $ratesQuantity={sectionRatesQuantity}
              data-test-id={PRODUCT_SUMMARY_TEST_IDS.NOT_ELIGIBLE_RATES}
            >
              <NavigationClose width="2.3rem" color={colors.red[600]} />
              {isRenewOrCancelProductLine ? 'Coverage canceled' : 'Not Eligible'}
            </NotEligibleRatesStyled>
          )}
        </RatesListStyled>

        {isRenewOrCancelProductLine ? (
          <BrowsingCriteriaBlockStyled data-test-id={PRODUCT_SUMMARY_TEST_IDS.EMPTY_BROWSING_CRITERIA_BLOCK}>
            <BrowsingCriteriaTextStyled>-</BrowsingCriteriaTextStyled>
          </BrowsingCriteriaBlockStyled>
        ) : (
          <BrowsingCriteriaBlockStyled data-test-id={PRODUCT_SUMMARY_TEST_IDS.EXISTING_BROWSING_CRITERIA_BLOCK}>
            {productSummary.rates.length > 0 ? (
              <BrowsingCriteriaTextStyled data-test-id={PRODUCT_SUMMARY_TEST_IDS.BROWSING_CRITERIA_TEXT}>
                {(productSummary.browsingCriteria as CartBrowsingCriteria).shortName[productLineId] || '-'}
              </BrowsingCriteriaTextStyled>
            ) : (
              <NotEligibleBrowsingCriteriaStyled data-test-id={PRODUCT_SUMMARY_TEST_IDS.NOT_ELIGIBLE_BROWSING_CRITERIA}>
                <NavigationClose width="2.3rem" color={colors.red[600]} />
                Not Eligible
              </NotEligibleBrowsingCriteriaStyled>
            )}
          </BrowsingCriteriaBlockStyled>
        )}
      </ProductSummaryStyled>
    );
  },
);

export default ProductSummary;
